/**
 * RP4 — Sibling DOM children of `<Canvas>` extracted from `Scene3DStage.tsx`.
 *
 * Three small render-only components / helpers:
 *
 *   - **LiveTextOverlays** — wraps the (possibly null) `liveTextInput` +
 *     `inPlaceEdit` props from Scene3DStageProps so the orchestrator
 *     doesn't carry two ternary blocks inline.
 *
 *   - **ArrayRectGripPlaceholders** — emits one `data-array-id` div per
 *     `Rectangular Associative Array` entity. PR-G's grip overlay
 *     attaches row/column chevrons to these anchors.
 *
 * # Why a dedicated module
 *
 * The JSX subtrees were ~50 LOC inline; hoisting them shrinks the
 * orchestrator and gives us a single import per concern.
 */

import * as React from "react";

import { LiveTextOverlay } from "../text/liveTextOverlay";
import type { Scene3DStageProps } from "../Scene3DStageProps";

interface LiveTextOverlaysProps {
  liveTextInput: Scene3DStageProps["liveTextInput"];
  inPlaceEdit: Scene3DStageProps["inPlaceEdit"];
}

/**
 * Render the two `LiveTextOverlay` instances (TEXT/MTEXT drafting +
 * in-place edit). Returns `<></>` when neither is active.
 */
export function LiveTextOverlays(
  props: LiveTextOverlaysProps,
): React.ReactElement {
  const { liveTextInput, inPlaceEdit } = props;
  return (
    <>
      {liveTextInput ? (
        <LiveTextOverlay
          text={liveTextInput.text}
          position={liveTextInput.position}
          height={liveTextInput.height}
          rotation={liveTextInput.rotation}
          multiline={liveTextInput.multiline}
          width={liveTextInput.width}
          onTextChange={liveTextInput.onTextChange}
          onSubmit={liveTextInput.onSubmit}
          onCancel={liveTextInput.onCancel}
        />
      ) : null}
      {inPlaceEdit ? (
        <LiveTextOverlay
          entityId={inPlaceEdit.entityId}
          text={inPlaceEdit.text}
          position={inPlaceEdit.position}
          height={inPlaceEdit.height}
          rotation={inPlaceEdit.rotation}
          multiline={inPlaceEdit.multiline}
          width={inPlaceEdit.width}
          onTextChange={inPlaceEdit.onTextChange}
          onSubmit={inPlaceEdit.onSubmit}
          onCancel={inPlaceEdit.onCancel}
        />
      ) : null}
    </>
  );
}

interface ArrayRectGripPlaceholdersProps {
  entities: Scene3DStageProps["entities"];
}

/**
 * PR-D + Q12.B — emit one anchor `<div>` per `Rectangular Associative
 * Array` entity. Each div has zero size + pointerEvents:none so it
 * never intercepts canvas clicks; it exists only as a DOM anchor for
 * the grip overlay to attach row/column chevrons.
 */
export function ArrayRectGripPlaceholders(
  props: ArrayRectGripPlaceholdersProps,
): React.ReactElement {
  return (
    <>
      {props.entities
        .filter((e) => e.type === "Rectangular Associative Array")
        .map((e) => (
          <div
            key={e.id}
            data-array-id={e.id}
            data-testid={`scene3d-array-${e.id}`}
            style={{
              position: "absolute",
              width: 0,
              height: 0,
              pointerEvents: "none",
            }}
          />
        ))}
    </>
  );
}
